import { useCallback, useMemo } from 'react';
import { createKeyframe, type AnimatableProperty } from '@cinegrid/core';
import { useEditorStore } from '../../state/editorStore';

const KF_EPS = 0.001;

export function hasKeyframeAt(
  keyframes: { property: string; time: number }[],
  property: AnimatableProperty,
  time: number
) {
  return keyframes.some(
    (k) => k.property === property && Math.abs(k.time - time) < KF_EPS
  );
}

export function useAnimatableParam(
  clipId: string | undefined,
  property: AnimatableProperty | AnimatableProperty[],
  value: number,
  commit: (v: number) => void
) {
  const playhead = useEditorStore((s) => s.playhead);
  const project = useEditorStore((s) => s.project);
  const updateClipProperties = useEditorStore((s) => s.updateClipProperties);

  const clip = useMemo(
    () => (clipId ? project.clips.find((c) => c.id === clipId) : undefined),
    [project.clips, clipId]
  );

  const props = useMemo(
    () => (Array.isArray(property) ? property : [property]),
    [property]
  );

  const localTime = clip ? Math.max(0, playhead - clip.start) : 0;
  const kfs = clip?.properties.keyframes ?? [];

  const keyframed = props.every((p) => hasKeyframeAt(kfs, p, localTime));
  const animated = props.some((p) => kfs.some((k) => k.property === p));

  const onToggleKeyframe = useCallback(() => {
    if (!clipId || !clip) return;
    let next = [...clip.properties.keyframes];
    if (keyframed) {
      next = next.filter(
        (k) =>
          !(
            props.includes(k.property as AnimatableProperty) &&
            Math.abs(k.time - localTime) < KF_EPS
          )
      );
      updateClipProperties(clipId, { keyframes: next });
      return;
    }
    for (const p of props) {
      next = next.filter(
        (k) => !(k.property === p && Math.abs(k.time - localTime) < KF_EPS)
      );
      next.push(createKeyframe(p, localTime, value));
    }
    updateClipProperties(clipId, { keyframes: next });
  }, [clipId, clip, keyframed, localTime, props, updateClipProperties, value]);

  const onChange = useCallback(
    (v: number) => {
      commit(v);
      if (!clipId || !clip) return;
      if (!animated && !keyframed) return;
      let next = [...clip.properties.keyframes];
      for (const p of props) {
        next = next.filter(
          (k) => !(k.property === p && Math.abs(k.time - localTime) < KF_EPS)
        );
        next.push(createKeyframe(p, localTime, v));
      }
      updateClipProperties(clipId, { keyframes: next });
    },
    [animated, clip, clipId, commit, keyframed, localTime, props, updateClipProperties]
  );

  return {
    keyframed,
    onToggleKeyframe: clipId ? onToggleKeyframe : undefined,
    onChange,
  };
}
